import { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import type { AlertEvent, AlertSeverity } from '@/types/alerts';
import { getAlertDisplayName, getAgentDisplayName } from '@/utils/alertTypes';
import { ALERTS_NAVIGATION_EVENT } from '@/utils/desktopNotification';
import { Snackbar } from '@gapartners/ui';

type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

function mapSeverity(severity: AlertSeverity): SnackbarSeverity {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'high':
    case 'medium':
      return 'warning';
    case 'low':
    default:
      return 'info';
  }
}

/** "Missed Call — John Doe · BasicQueue" (agent and queue omitted when absent). */
function formatAlertMessage(alert: AlertEvent): string {
  const agent = getAgentDisplayName(alert);
  const details = [agent !== '-' ? agent : null, alert.queueName]
    .filter(Boolean)
    .join(' · ');
  return details
    ? `${getAlertDisplayName(alert)} — ${details}`
    : getAlertDisplayName(alert);
}

function SingleAlertSnackbar({ alert }: { alert: AlertEvent }) {
  const [open, setOpen] = useState(true);
  const handleClose = () => setOpen(false);
  const handleClick = () => {
    window.dispatchEvent(new Event(ALERTS_NAVIGATION_EVENT));
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      message={
        <span onClick={handleClick} style={{ cursor: 'pointer' }}>
          {formatAlertMessage(alert)}
        </span>
      }
      variant="toast"
      severity={mapSeverity(alert.severity)}
      autoHideDuration={5000}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      showCloseButton
    />
  );
}

/**
 * Bottom-right toast for the most recent active alert.
 * Auto-hides after 5s; the alert remains in the Alerts list (Header bell + AlertsView).
 */
export function LiveAlertsPopupStack() {
  const { activeAlerts } = useAppSelector(state => state.alerts);
  const latest = activeAlerts[0];

  if (!latest) {
    return null;
  }

  // Key by alert id so a new alert remounts and re-opens the toast.
  return <SingleAlertSnackbar key={latest.id} alert={latest} />;
}
